import { motion } from 'framer-motion'
import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { Icon, Spinner } from './Icon'

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-lime text-ink-950 font-semibold hover:bg-[#e4ff6b] active:bg-lime-deep shadow-[0_10px_30px_-12px_rgba(215,255,62,0.7)]',
  outline: 'border border-ink-600 text-fg hover:border-ink-600/80 hover:bg-ink-800/70',
  ghost: 'text-fg-muted hover:text-fg hover:bg-ink-800/70',
  danger: 'bg-danger/12 text-danger border border-danger/35 hover:bg-danger/20',
}

// Every size clears the 44px minimum touch target, including `sm`.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-11 px-4 text-sm rounded-xl',
  md: 'h-12 px-5 text-[0.9375rem] rounded-xl',
  lg: 'h-14 px-7 text-base rounded-2xl',
}

/**
 * Framer redefines the drag and animation event handlers with its own
 * signatures, so the DOM versions have to be dropped before they can be
 * forwarded to `motion.button`.
 */
type ForwardableButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd'
  | 'onDragEnter'
  | 'onDragLeave'
  | 'onDragOver'
  | 'onDrop'
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'style'
>

interface ButtonProps extends ForwardableButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, fullWidth, className, children, disabled, ...rest },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      // Scale-only press feedback: it cannot shift neighbouring layout the way
      // a border or padding change would.
      whileTap={disabled || loading ? undefined : { scale: 0.975 }}
      transition={{ type: 'spring', stiffness: 620, damping: 32 }}
      disabled={disabled || loading}
      className={cx(
        'no-tap-delay inline-flex items-center justify-center gap-2 transition-colors duration-200',
        'disabled:opacity-40 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={17} /> : icon}
      {children}
    </motion.button>
  )
})

// ---------------------------------------------------------------------------
// Text field
// ---------------------------------------------------------------------------

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string
  error?: string | null
  hint?: string
  icon?: ReactNode
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, icon, type = 'text', className, id, ...rest },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
  const [revealed, setRevealed] = useState(false)
  const isPassword = type === 'password'

  return (
    <div className="w-full">
      {/* A real label, not a placeholder - placeholders vanish the moment you
          start typing, exactly when the field most needs identifying. */}
      <label htmlFor={fieldId} className="eyebrow mb-2 block text-fg-muted">
        {label}
      </label>

      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-fg-dim">
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          type={isPassword && revealed ? 'text' : type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            'h-13 w-full rounded-xl border bg-ink-950/60 py-3.5 text-fg transition-colors duration-200',
            'placeholder:text-fg-dim/70 focus:outline-none',
            icon ? 'pl-12' : 'pl-4',
            isPassword ? 'pr-12' : 'pr-4',
            error
              ? 'border-danger/60 focus:border-danger'
              : 'border-ink-700 focus:border-lime/60 hover:border-ink-600',
            className,
          )}
          style={{ minHeight: 52 }}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-fg-dim transition-colors hover:text-fg"
          >
            <Icon name={revealed ? 'eyeOff' : 'eye'} size={18} />
          </button>
        )}
      </div>

      {/* Errors sit next to the field they belong to, and announce themselves. */}
      {error ? (
        <p id={`${fieldId}-error`} role="alert" className="mt-2 flex items-start gap-1.5 text-sm text-danger">
          <Icon name="close" size={15} className="mt-0.5 shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-2 text-sm text-fg-dim">
          {hint}
        </p>
      ) : null}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Segmented control
// ---------------------------------------------------------------------------

export interface SegmentedOption<T extends string | number> {
  value: T
  label: string
  sublabel?: string
  disabled?: boolean
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
  columns,
}: {
  options: SegmentedOption<T>[]
  value: T
  onChange: (next: T) => void
  label: string
  columns?: number
}) {
  const groupId = useId()
  return (
    <div>
      <p className="eyebrow mb-2.5" id={groupId}>
        {label}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={groupId}
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columns ?? options.length}, minmax(0, 1fr))` }}
      >
        {options.map((option) => {
          const selected = option.value === value
          return (
            <button
              key={String(option.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={option.disabled}
              onClick={() => onChange(option.value)}
              className={cx(
                'no-tap-delay relative flex min-h-[52px] flex-col items-center justify-center rounded-xl border px-3 py-2.5 transition-all duration-200',
                selected
                  ? 'border-lime/55 bg-lime-wash text-fg'
                  : 'border-ink-700 text-fg-muted hover:border-ink-600 hover:text-fg',
                option.disabled && 'cursor-not-allowed opacity-35',
              )}
            >
              <span className={cx('text-sm font-semibold', selected && 'text-lime')}>
                {option.label}
              </span>
              {option.sublabel && (
                <span className="mt-0.5 text-[0.6875rem] leading-tight text-fg-dim">
                  {option.sublabel}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Coin chip
// ---------------------------------------------------------------------------

export function CoinChip({
  amount,
  size = 'md',
  tone = 'default',
}: {
  amount: number
  size?: 'sm' | 'md' | 'lg'
  tone?: 'default' | 'gold' | 'muted'
}) {
  const sizes = {
    sm: 'text-xs gap-1.5 px-2.5 py-1',
    md: 'text-sm gap-2 px-3 py-1.5',
    lg: 'text-lg gap-2.5 px-4 py-2',
  }[size]

  const tones = {
    default: 'border-ink-700 bg-ink-850 text-fg',
    gold: 'border-gold/30 bg-gold/10 text-gold',
    muted: 'border-transparent bg-transparent text-fg-dim',
  }[tone]

  return (
    <span
      className={cx('inline-flex items-center rounded-full border font-mono font-medium tnum', sizes, tones)}
    >
      <Icon name="coin" size={size === 'lg' ? 18 : size === 'md' ? 15 : 13} />
      {amount.toLocaleString()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export function Panel({
  children,
  className,
  flush,
}: {
  children: ReactNode
  className?: string
  flush?: boolean
}) {
  return <div className={cx(flush ? 'panel-flush' : 'panel', className)}>{children}</div>
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <p className="display-title text-sm text-fg">{title}</p>
      <p className="mt-2.5 max-w-sm text-sm leading-relaxed text-fg-dim">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  )
}

export { cx }
